// ─────────────────────────────────────────────────────────────
// ZIFEK — Explorateur de base de données (SuperAdmin, lecture seule)
// Passerelle front vers l'edge function `db-export` + téléchargements.
// ─────────────────────────────────────────────────────────────
import JSZip from 'jszip';
import { supabase } from '@/lib/supabase';

export interface DbColumn {
  name: string;
  data_type: string;
  udt_name: string;
  is_nullable: boolean;
  col_default: string | null;
  max_length: number | null;
}

export interface DbTable {
  name: string;
  columns: DbColumn[];
  primary_key: string[];
  row_count: number;
}

export type ExportFormat = 'sql' | 'mysql' | 'json' | 'csv';

export interface CallerIdentity {
  userId: number;
  email: string;
}

async function callDbExport<T>(identity: CallerIdentity, body: Record<string, unknown>): Promise<T> {
  const { data, error } = await supabase.functions.invoke('db-export', {
    body: { ...body, user_id: identity.userId, email: identity.email },
  });
  if (error) throw new Error(error.message || 'Erreur de communication avec le back-end.');
  if (data && typeof data === 'object' && 'error' in data && data.error) {
    throw new Error(String((data as { error: unknown }).error));
  }
  return data as T;
}

export async function fetchDbOverview(identity: CallerIdentity): Promise<{ tables: DbTable[]; total: number }> {
  return callDbExport<{ tables: DbTable[]; total: number }>(identity, { action: 'overview' });
}

export async function fetchTablePreview(
  identity: CallerIdentity,
  table: string,
  limit = 25,
): Promise<{ table: string; columns: DbColumn[]; rows: Record<string, unknown>[] }> {
  return callDbExport(identity, { action: 'preview', table, limit });
}

export interface ExportResult {
  format: string;
  filename?: string;
  content?: string;
  files?: { name: string; content: string }[];
}

export async function runDatabaseExport(
  identity: CallerIdentity,
  format: ExportFormat,
  tables?: string[],
): Promise<ExportResult> {
  return callDbExport<ExportResult>(identity, { action: 'export', format, tables });
}

/** Déclenche le téléchargement d'un contenu texte. */
export function downloadText(filename: string, content: string, mime = 'text/plain'): void {
  const blob = new Blob([content], { type: `${mime};charset=utf-8` });
  triggerBlobDownload(filename, blob);
}

/** Empaquette les CSV (un fichier par table) dans un ZIP puis télécharge. */
export async function downloadCsvZip(files: { name: string; content: string }[]): Promise<void> {
  const zip = new JSZip();
  const folder = zip.folder('zifek-database-csv');
  files.forEach((f) => folder?.file(f.name, f.content));
  const blob = await zip.generateAsync({ type: 'blob' });
  triggerBlobDownload(`zifek-database-${new Date().toISOString().slice(0, 10)}.zip`, blob);
}

export function triggerBlobDownload(filename: string, blob: Blob): void {
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
}